import {
  ApiError,
  formatMoney,
  type ProductAttributeDefinition,
  type ProductAttributePrice,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { api } from '../lib/api.js'

interface AttrRow {
  id: number
  key: string
  value: string
}

interface ComboRow {
  id: number
  attrs: AttrRow[]
  priceDollars: string
}

/**
 * Full prices for a model + attribute combination (t1158): a TCL that carries
 * Waze sells for its own price, not the normal price plus a surcharge. When a
 * device with those attributes is picked on a new sale, the sale starts at
 * this price; a device with no matching combination sells at the model's
 * normal price. Where a device matches several rows, the one naming the most
 * attributes wins.
 *
 * A blank value means "has this attribute" (the Waze checkbox in Provisioning);
 * type a value for a text attribute (Color: Pink).
 */
export function AttributePricesDialog({
  product,
  onClose,
}: {
  product: { id: string; name: string; priceCents: number } | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const nextId = useRef(0)
  const listId = useId()
  const [rows, setRows] = useState<ComboRow[]>([])

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.products.attributePrices(product?.id ?? ''),
    queryFn: () => api.get<ProductAttributePrice[]>(`/products/${product?.id}/attribute-prices`),
    enabled: product !== null,
  })
  const { data: definitions } = useQuery({
    queryKey: queryKeys.products.attributeDefinitions(),
    queryFn: () => api.get<ProductAttributeDefinition[]>('/product-attributes'),
    staleTime: 60_000,
    enabled: product !== null,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: seed the form once per loaded result; nextId is a ref
  useEffect(() => {
    if (!data) return
    setRows(
      data.map((combo) => ({
        id: nextId.current++,
        priceDollars: (combo.priceCents / 100).toFixed(2),
        attrs: Object.entries(combo.attributes).map(([key, value]) => ({
          id: nextId.current++,
          key,
          value: value === true ? '' : String(value),
        })),
      })),
    )
  }, [data])

  function patchRow(id: number, patch: Partial<ComboRow>) {
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }

  function patchAttr(rowId: number, attrId: number, patch: Partial<AttrRow>) {
    setRows((current) =>
      current.map((r) =>
        r.id === rowId
          ? { ...r, attrs: r.attrs.map((a) => (a.id === attrId ? { ...a, ...patch } : a)) }
          : r,
      ),
    )
  }

  function addCombo() {
    setRows((current) => [
      ...current,
      {
        id: nextId.current++,
        priceDollars: '',
        attrs: [{ id: nextId.current++, key: '', value: '' }],
      },
    ])
  }

  // Rows left half-filled are reported rather than silently dropped, so a
  // price someone typed never just vanishes on Save.
  function buildPrices(): { prices: ProductAttributePrice[]; problem: string | null } {
    const prices: ProductAttributePrice[] = []
    for (const row of rows) {
      const attributes: Record<string, string | true> = {}
      for (const a of row.attrs) {
        const key = a.key.trim()
        if (!key) continue
        const value = a.value.trim()
        if (value.toLowerCase() === 'false') {
          return { prices, problem: 'Use a combination of attributes a device has, not "false".' }
        }
        attributes[key] = value === '' || value.toLowerCase() === 'true' ? true : value
      }
      const price = Number(row.priceDollars)
      if (Object.keys(attributes).length === 0) {
        return { prices, problem: 'Every price needs at least one attribute.' }
      }
      if (row.priceDollars.trim() === '' || !Number.isFinite(price) || price < 0) {
        return { prices, problem: 'Enter a price for every combination.' }
      }
      prices.push({ attributes, priceCents: Math.round(price * 100) })
    }
    return { prices, problem: null }
  }

  const { prices, problem } = buildPrices()

  const save = useMutation({
    mutationFn: () =>
      api.put<ProductAttributePrice[]>(`/products/${product?.id}/attribute-prices`, { prices }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.products.attributePrices(product?.id ?? ''),
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Attribute prices saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not save attribute prices',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={product !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attribute prices — {product?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            Set the full price for this model when a device has certain attributes. It replaces the
            normal price of {product ? formatMoney(product.priceCents) : ''}; it is not added to it.
            Devices without a matching combination keep the normal price.
          </p>

          {isLoading ? null : rows.length === 0 ? (
            <p className="text-sm text-foreground-subtle">No attribute prices yet.</p>
          ) : (
            rows.map((row) => (
              <div key={row.id} className="space-y-2 rounded-md border border-border p-3">
                {row.attrs.map((a) => (
                  <div key={a.id} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                    <Input
                      placeholder="Attribute (e.g. Waze)"
                      value={a.key}
                      list={`${listId}-names`}
                      autoComplete="off"
                      onChange={(e) => patchAttr(row.id, a.id, { key: e.target.value })}
                    />
                    <Input
                      placeholder="Value (blank = has it)"
                      value={a.value}
                      autoComplete="off"
                      onChange={(e) => patchAttr(row.id, a.id, { value: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove attribute"
                      disabled={row.attrs.length === 1}
                      onClick={() =>
                        patchRow(row.id, { attrs: row.attrs.filter((x) => x.id !== a.id) })
                      }
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      patchRow(row.id, {
                        attrs: [...row.attrs, { id: nextId.current++, key: '', value: '' }],
                      })
                    }
                  >
                    <Plus /> Another attribute
                  </Button>
                  <Field label="Full price ($)" className="ms-auto w-32">
                    {(props) => (
                      <Input
                        {...props}
                        inputMode="decimal"
                        value={row.priceDollars}
                        onChange={(e) => patchRow(row.id, { priceDollars: e.target.value })}
                      />
                    )}
                  </Field>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove this price"
                    onClick={() => setRows((current) => current.filter((r) => r.id !== row.id))}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}

          <datalist id={`${listId}-names`}>
            {(definitions ?? []).map((d) => (
              <option key={d.id} value={d.name} />
            ))}
          </datalist>

          <Button type="button" variant="secondary" size="sm" onClick={addCombo}>
            <Plus /> Add a price
          </Button>
          {problem ? <p className="text-sm text-danger">{problem}</p> : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={problem !== null}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
